import { type Router } from 'vue-router';

import { isArrayOfNonEmptyStrings } from '@/core/lib/AssertUtils';
import { isDeepEqual } from '@/core/lib/ObjectUtils';
import { AccessDeniedRouteError, getRouteNameFromMaybeError } from '@/core/lib/RouterUtils';
import { RouteName } from '@/core/router/RouteName';
import { sanitizeAuthenticatedRoute } from '@/core/router/RouterAuthSanitize';
import {
  getAuthentication,
  isAuthenticationFresh,
  isTokenRefreshed,
} from '@/plugins/authentication/lib/Authentication';
import { hasScope } from '@/plugins/authentication/lib/UserPermissions';
import { trackEvent } from '@/plugins/gtmTracker/lib/GtmTracker';
import { getCompte } from '@/plugins/utilisateurs/lib/UtilisateurApi';

export const addNavigationGuards = (router: Router): void => {
  router.beforeEach(async (to, from, next) => {
    if (to.meta.requiresAuth) {
      const { authenticated } = await (await getAuthentication()).authenticate();

      if (!authenticated) {
        next(new AccessDeniedRouteError());

        return;
      }
    }

    if (isArrayOfNonEmptyStrings(to.meta.requiresScopes)) {
      for (const requiredScope of to.meta.requiresScopes) {
        const hasRequiredScope = await hasScope(requiredScope);

        if (!hasRequiredScope) {
          next({ name: RouteName.ERROR_ACCESS_DENIED });

          return;
        }
      }
    }

    next();
  });

  router.beforeEach(async (to, from, next) => {
    const { getState } = await getAuthentication();

    if (getState().authenticated) {
      const sanitizedAuthenticatedRoute = sanitizeAuthenticatedRoute(to);

      if (sanitizedAuthenticatedRoute) {
        next(sanitizedAuthenticatedRoute);

        return;
      }
    }

    next();
  });

  router.beforeEach(async (to, from, next) => {
    const { getState } = await getAuthentication();

    const state = getState();
    const compte = await getCompte();

    if (
      state.authenticated &&
      !state.user?.isImpersonated &&
      compte &&
      !compte.conditionsGeneralesUtilisation?.isDerniereVersionAcceptee &&
      to.name !== RouteName.CONDITIONS_GENERALES_UTILISATION_ACCEPTER
    ) {
      next({
        name: RouteName.CONDITIONS_GENERALES_UTILISATION_ACCEPTER,
        query: {
          next: to.fullPath,
        },
      });

      return;
    }

    next();
  });

  router.afterEach(async (to, from) => {
    if (
      (to.name !== from.name || !isDeepEqual(to.params, from.params)) &&
      (await isTokenRefreshed() || !await isAuthenticationFresh())
    ) {
      trackEvent({
        'event': 'content-view',
        'content-name': to.path,
        'content-view-name': to.name,
      });
    }
  });

  router.onError(error => {
    return router.push(getRouteNameFromMaybeError(error));
  });
};
